// Porta JS do módulo do back-end (`src/project/disbursement/profile.js`).
// Mesmos nomes, mesmas mensagens, mesmas invariantes: o editor não pode aceitar
// um perfil que a rota vai recusar.

export const MIN_OFFSET = -12;
export const MAX_OFFSET = 36;
export const MAX_LINES = 60;
export const AT_SIGHT_PROFILE = Object.freeze([
  Object.freeze({ offsetMonths: 0, percentBps: 10000 }),
]);

export class ProfileError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = "ProfileError";
    this.errorCode = errorCode;
    this.statusCode = 400;
  }
}

const UNITS = new Set(["months", "calendar_days", "business_days"]);
const UNIT_ORDER = Object.freeze({ months: 0, calendar_days: 1, business_days: 2 });

// Lê as duas formas de linha aceitas — a legada `{ offsetMonths, percentBps }` (unidade
// implícita "months") ou a nova `{ offset, unit, percentBps }` — para um formato interno
// único `{ unit, offset, percentBps }`. Devolve `null` em qualquer forma inválida.
const normalizeLine = (line) => {
  if (!line || typeof line !== "object") return null;
  const { percentBps } = line;
  if (!Number.isSafeInteger(percentBps) || percentBps <= 0) return null;
  const hasLegacyOffset = Object.prototype.hasOwnProperty.call(line, "offsetMonths");
  const hasNewOffset = Object.prototype.hasOwnProperty.call(line, "offset");
  if (hasLegacyOffset === hasNewOffset) return null; // exatamente uma das duas formas
  if (hasLegacyOffset) {
    if (line.unit !== undefined) return null; // a forma legada nunca carrega unidade
    if (!Number.isSafeInteger(line.offsetMonths)) return null;
    return { unit: "months", offset: line.offsetMonths, percentBps };
  }
  if (!UNITS.has(line.unit) || !Number.isSafeInteger(line.offset)) return null;
  return { unit: line.unit, offset: line.offset, percentBps };
};

// `{ offsetMonths, percentBps }` para uma linha em meses (forma idêntica à anterior),
// `{ offset, unit, percentBps }` para uma linha em dias.
const toPublicLine = ({ unit, offset, percentBps }) => (
  unit === "months" ? { offsetMonths: offset, percentBps } : { offset, unit, percentBps }
);

const unitOf = (line) => line.unit ?? "months";
const offsetOf = (line) => (line.unit === undefined ? line.offsetMonths : line.offset);

const compareLines = (left, right) => (
  UNIT_ORDER[left.unit] - UNIT_ORDER[right.unit] || left.offset - right.offset
);

// Envelope analítico (D6): valida uma linha em dias contra a janela de meses sem
// conhecer a competência de execução real — `ceil(D/28)` e `floor(D/31)` são os dois
// extremos (mês mais curto e mais longo), então o envelope é conservador por construção
// e a geração (monthOffsetsOf em engine.js) nunca cai fora de [MIN_OFFSET, MAX_OFFSET]
// para uma linha aceita aqui.
const CALENDAR_DAYS_MIN_MONTH_LENGTH = 28;
const CALENDAR_DAYS_MAX_MONTH_LENGTH = 31;

const calendarDaysMonthEnvelope = (calendarDays) => {
  if (calendarDays >= 0) {
    return [
      Math.floor(calendarDays / CALENDAR_DAYS_MAX_MONTH_LENGTH),
      Math.ceil(calendarDays / CALENDAR_DAYS_MIN_MONTH_LENGTH),
    ];
  }
  const magnitude = -calendarDays;
  return [
    -Math.ceil(magnitude / CALENDAR_DAYS_MIN_MONTH_LENGTH),
    -Math.floor(magnitude / CALENDAR_DAYS_MAX_MONTH_LENGTH),
  ];
};

// Uma defasagem em dias úteis converte para o pior caso em dias corridos antes do mesmo
// envelope (D6). Com o calendário fixo de feriados de calendar.js, nenhuma semana ISO
// perde mais de dois dias úteis para um feriado nacional (o par mais próximo, 15 e 20 de
// novembro, fica a 5 dias de distância), então uma semana nunca tem menos de 3 dias
// úteis — esse piso, não a semana comum de 5 dias úteis, é o que torna a conversão um
// limite superior de verdade.
const MIN_BUSINESS_DAYS_PER_WEEK = 3;
const CALENDAR_DAYS_PER_WEEK = 7;

const worstCaseCalendarDays = (businessDays) => {
  const magnitude = Math.abs(businessDays);
  const weeks = Math.ceil(magnitude / MIN_BUSINESS_DAYS_PER_WEEK);
  const calendarMagnitude = weeks * CALENDAR_DAYS_PER_WEEK;
  return businessDays < 0 ? -calendarMagnitude : calendarMagnitude;
};

const monthEnvelopeOf = (unit, offset) => {
  if (unit === "months") return [offset, offset];
  const calendarDays = unit === "calendar_days" ? offset : worstCaseCalendarDays(offset);
  return calendarDaysMonthEnvelope(calendarDays);
};

const isOffsetInRange = (unit, offset) => {
  const [min, max] = monthEnvelopeOf(unit, offset);
  return min >= MIN_OFFSET && max <= MAX_OFFSET;
};

// O `[min, max]` mais largo que a defasagem de uma linha (na `unit` dela) pode assumir e
// ainda ser aceita por `validateProfile`, para qualquer competência de origem — o limite
// exato que `isOffsetInRange` aplica, em forma fechada. Usado para mostrar a faixa aceita
// no campo antes de o cliente digitar um valor que a rota recusaria (D6).
export const offsetRangeFor = (unit) => {
  if (unit === "months") return { min: MIN_OFFSET, max: MAX_OFFSET };
  const factor = unit === "calendar_days"
    ? CALENDAR_DAYS_MIN_MONTH_LENGTH
    : (MIN_BUSINESS_DAYS_PER_WEEK * CALENDAR_DAYS_MIN_MONTH_LENGTH) / CALENDAR_DAYS_PER_WEEK;
  return { min: MIN_OFFSET * factor, max: MAX_OFFSET * factor };
};

export const validateProfile = (profile) => {
  if (!Array.isArray(profile) || profile.length === 0) {
    throw new ProfileError("profile_empty", "A forma de desembolso precisa de pelo menos uma linha");
  }
  if (profile.length > MAX_LINES) {
    throw new ProfileError("profile_too_long", `A forma de desembolso aceita no máximo ${MAX_LINES} linhas`);
  }
  const normalized = profile.map(normalizeLine);
  if (normalized.some((line) => line === null)) {
    throw new ProfileError("profile_line_invalid", "Cada linha precisa de defasagem inteira e percentual positivo");
  }
  const seen = new Set();
  let sum = 0;
  for (const line of normalized) {
    if (!isOffsetInRange(line.unit, line.offset)) {
      throw new ProfileError("profile_offset_range", `A defasagem deve ficar entre ${MIN_OFFSET} e ${MAX_OFFSET} meses`);
    }
    const key = `${line.unit}:${line.offset}`;
    if (seen.has(key)) {
      throw new ProfileError("profile_offset_duplicate", "Cada defasagem só pode aparecer uma vez");
    }
    seen.add(key);
    sum += line.percentBps;
  }
  if (sum !== 10000) {
    throw new ProfileError("profile_sum", "Os percentuais precisam somar exatamente 100%");
  }
  return normalized.sort(compareLines).map(toPublicLine);
};

const assertInstallments = (unit, installments, startOffset) => {
  if (!Number.isSafeInteger(installments) || installments < 1) {
    throw new ProfileError("profile_line_invalid", "Número de parcelas inválido");
  }
  if (!Number.isSafeInteger(startOffset)) {
    throw new ProfileError("profile_line_invalid", "Defasagem inicial inválida");
  }
  if (!isOffsetInRange(unit, startOffset) || !isOffsetInRange(unit, startOffset + installments - 1)) {
    throw new ProfileError("profile_offset_range", `As parcelas precisam caber entre ${MIN_OFFSET} e ${MAX_OFFSET} meses`);
  }
};

const splitBps = (count) => {
  const base = Math.floor(10000 / count);
  const remainder = 10000 - base * count;
  return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
};

const lineAt = (unit, offset, percentBps) => (
  unit === "months" ? { offsetMonths: offset, percentBps } : { offset, unit, percentBps }
);

export const equalInstallments = ({ installments, startOffset = 0, unit = "months" } = {}) => {
  assertInstallments(unit, installments, startOffset);
  return validateProfile(
    splitBps(installments).map((percentBps, index) => lineAt(unit, startOffset + index, percentBps)),
  );
};

export const downPaymentPlusInstallments = ({
  downPaymentBps,
  downPaymentOffset,
  installments,
  startOffset = 0,
  unit = "months",
} = {}) => {
  if (!Number.isSafeInteger(downPaymentBps) || downPaymentBps <= 0 || downPaymentBps >= 10000) {
    throw new ProfileError("profile_line_invalid", "A entrada precisa ficar entre 1% e 99%");
  }
  if (!Number.isSafeInteger(downPaymentOffset) || !isOffsetInRange(unit, downPaymentOffset)) {
    throw new ProfileError("profile_offset_range", `A defasagem da entrada deve ficar entre ${MIN_OFFSET} e ${MAX_OFFSET} meses`);
  }
  assertInstallments(unit, installments, startOffset);
  const rest = 10000 - downPaymentBps;
  const base = Math.floor(rest / installments);
  const remainder = rest - base * installments;
  const lines = [lineAt(unit, downPaymentOffset, downPaymentBps)];
  for (let index = 0; index < installments; index += 1) {
    lines.push(lineAt(unit, startOffset + index, base + (index < remainder ? 1 : 0)));
  }
  return validateProfile(lines);
};

const percentText = (bps) => {
  const value = bps / 100;
  return `${Number.isInteger(value) ? value : value.toFixed(2).replace(".", ",")}%`;
};

const UNIT_LABELS = Object.freeze({
  months: Object.freeze({ singular: "mês", plural: "meses" }),
  calendar_days: Object.freeze({ singular: "dia corrido", plural: "dias corridos" }),
  business_days: Object.freeze({ singular: "dia útil", plural: "dias úteis" }),
});

const offsetText = (offset, unit) => {
  if (offset === 0) return "na execução";
  const abs = Math.abs(offset);
  const { singular, plural } = UNIT_LABELS[unit];
  const label = abs === 1 ? singular : plural;
  return offset < 0 ? `${abs} ${label} antes` : `${abs} ${label} depois`;
};

const describeLine = (line) => `${percentText(line.percentBps)} ${offsetText(offsetOf(line), unitOf(line))}`;

export const describeProfile = (profile) => {
  const lines = validateProfile(profile);
  if (lines.length === 1 && unitOf(lines[0]) === "months" && offsetOf(lines[0]) === 0) return "à vista";
  // Detecta uma sequência final de defasagens consecutivas, NA MESMA UNIDADE, com bps
  // parecidos, como "N parcelas".
  const runStart = (() => {
    let start = lines.length - 1;
    while (start > 0) {
      const previous = lines[start - 1];
      const current = lines[start];
      if (
        unitOf(current) !== unitOf(previous)
        || offsetOf(current) !== offsetOf(previous) + 1
        || Math.abs(current.percentBps - previous.percentBps) > 1
      ) break;
      start -= 1;
    }
    return start;
  })();
  const head = lines.slice(0, runStart);
  const run = lines.slice(runStart);
  const parts = head.map(describeLine);
  if (run.length >= 2) {
    const runBps = run.reduce((sum, line) => sum + line.percentBps, 0);
    const firstUnit = unitOf(run[0]);
    const from = (offsetOf(run[0]) === 0 && firstUnit === "months")
      ? "a partir da execução"
      : `a partir de ${offsetText(offsetOf(run[0]), firstUnit)}`;
    parts.push(`${percentText(runBps)} em ${run.length} parcelas ${from}`);
  } else {
    parts.push(...run.map(describeLine));
  }
  return parts.join(" · ");
};
